import { useQueries, useQueryClient } from '@tanstack/react-query';
import { components, paths } from 'src/generated/v1';
import { useMemo } from 'react';
import { $api, $apiFetchClient } from './api';

export type Product = components['schemas']['Product'];
export type ProductIn = components['schemas']['ProductIn'];
export type ProductUpdateIn = components['schemas']['ProductUpdateIn'];
export type Insert = components['schemas']['Insert'];

export const invalidateProductQueries = (queryClient: ReturnType<typeof useQueryClient>): void => {
  const keys = [
    ['get', '/products'],
    ['get', '/products/{product_id}'],
    ['get', '/products/{product_id}/boms/'],
    ['get', '/products/boms/{bom_id}/lines/'],
    ['get', '/stock/inventory'],
    ['get', '/stock/inventory/{product_id}'],
    ['get', '/inserts'],
    ['get', '/utility/models/quick_filter/{model}'],
  ];

  keys.forEach(key => {
    queryClient.invalidateQueries({ queryKey: key });
  });
};

export const useAllProducts = (options?: paths['/products']['get']['parameters']['query']) => {
  const allProductsQuery = $api.useQuery('get', '/products', {
    params: {
      query: options,
    },
  });

  return {
    items: allProductsQuery.data?.items ?? undefined,
    pages: allProductsQuery.data?.pages ?? undefined,
    total: allProductsQuery.data?.total ?? undefined,
    isLoading: allProductsQuery.isLoading,
    isError: allProductsQuery.isError,
    error: allProductsQuery.error,
    refetchProducts: allProductsQuery.refetch,
  };
};

export const useProducts = (productIds: number[]) => {
  const queries = useMemo(
    () =>
      productIds.map(id => ({
        queryKey: ['get', '/products/{product_id}', { params: { path: { product_id: id } } }],
        queryFn: async () =>
          $apiFetchClient.GET('/products/{product_id}', {
            params: { path: { product_id: id } },
          }),
      })),
    [productIds.join()],
  );

  return useQueries({ queries });
};

// Mutations only — doesn't trigger a product fetch. Use this when you only need to update/upload.
export const useProductMutations = () => {
  const queryClient = useQueryClient();

  const updateProductMutation = $api.useMutation('put', '/products/{product_id}', {
    onSuccess() {
      invalidateProductQueries(queryClient);
    },
  });

  const updateProductImageMutation = $api.useMutation('post', '/products/{product_id}/image', {
    onSuccess() {
      invalidateProductQueries(queryClient);
    },
  });

  return {
    updateProduct: updateProductMutation.mutate,
    updateProductAsync: updateProductMutation.mutateAsync,
    updateProductImage: updateProductImageMutation.mutate,
    updateProductImageAsync: updateProductImageMutation.mutateAsync,
    isUploadingImage: updateProductImageMutation.isPending,
    isUpdating: updateProductMutation.isPending,
    updateError: updateProductMutation.error,
  };
};

// Hook for managing an individual product (fetches product details)
export const useProduct = (productId: number) => {
  const productQuery = $api.useQuery('get', '/products/{product_id}', {
    params: {
      path: {
        product_id: productId,
      },
    },
  });

  const mutations = useProductMutations();

  return {
    product: productQuery.data,
    isLoading: productQuery.isLoading,
    isError: productQuery.isError,
    error: productQuery.error,
    refetchProduct: productQuery.refetch,
    ...mutations,
  };
};

export const useConfirmProductBulk = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('put', '/utility/bulk_action/{model}/confirm', {
    onSuccess() {
      invalidateProductQueries(queryClient);
    },
  });
};

// Hook for confirming a product
export const useConfirmProduct = () => {
  const queryClient = useQueryClient();

  const confirmProductMutation = $api.useMutation('post', '/products/{product_id}/confirm', {
    onSuccess() {
      invalidateProductQueries(queryClient);
    },
  });

  return {
    confirmProduct: confirmProductMutation.mutate,
    confirmProductAsync: confirmProductMutation.mutateAsync,
    isConfirming: confirmProductMutation.isPending,
    confirmError: confirmProductMutation.error,
  };
};

// Hook for setting a product back to draft
export const useSetProductToDraft = () => {
  const queryClient = useQueryClient();

  const setToDraftMutation = $api.useMutation('post', '/products/{product_id}/set_to_draft', {
    onSuccess() {
      invalidateProductQueries(queryClient);
    },
  });

  return {
    setProductToDraft: setToDraftMutation.mutate,
    setProductToDraftAsync: setToDraftMutation.mutateAsync,
    isSettingToDraft: setToDraftMutation.isPending,
    setToDraftError: setToDraftMutation.error,
  };
};

// Hook for creating a new product
export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  const createProductMutation = $api.useMutation('post', '/products', {
    onSuccess() {
      invalidateProductQueries(queryClient);
    },
  });

  return {
    createProduct: createProductMutation.mutate,
    createProductAsync: createProductMutation.mutateAsync,
    isCreating: createProductMutation.isPending,
    createError: createProductMutation.error,
  };
};

// Hook for deleting a product
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  const deleteProductMutation = $api.useMutation('delete', '/products/{product_id}', {
    onSuccess() {
      invalidateProductQueries(queryClient);
    },
  });

  return {
    deleteProduct: deleteProductMutation.mutate,
    deleteProductAsync: deleteProductMutation.mutateAsync,
    isDeleting: deleteProductMutation.isPending,
    deleteError: deleteProductMutation.error,
  };
};

export const useInsertProducts = () => {
  const insertProductsQuery = $api.useQuery('get', '/inserts');

  return {
    items: insertProductsQuery.data ?? undefined,
    isLoading: insertProductsQuery.isLoading,
    isError: insertProductsQuery.isError,
    error: insertProductsQuery.error,
    refetchInsertProducts: insertProductsQuery.refetch,
  };
};
